import React from 'react';
import clsx from 'clsx';
import {
  reservationStore,
  Step1Summary,
  Step2Summary,
  Step3Summary,
  StepSummary,
} from '../../state/reservation-store';
import { displayDate } from '@camp67/model/booking/dates';
import { Tooltip } from '../tooltip';

type StepNumber = 1 | 2 | 3;
export type Step = `step-${StepNumber}`;

export interface BookingFormSectionMessages {
  stepTitle: string;
  nextButton: string;
  continueButton: string;
  resetButton: string;
  expandTooltip: string;
  collapseTooltip: string;
  disabledTooltip: string;
  stepDisabledTooltip: string;
  // Step 1 summary
  step1SummaryRooms: string;
  step1SummaryRoomsEmpty: string;
  // Step 2 summary
  step2SummaryDates: string;
  step2SummaryDatesEmptyStart: string;
  step2SummaryDatesEmptyEnd: string;
  step2SummaryDatesValue: string;
  step2SummaryGuests: string;
  step2SummaryGuestsEmpty: string;
  // Step 3 summary
  step3SummaryGuests: string;
  step3SummaryGuestsEmpty: string;
  step3SummaryPrimary: string;
  step3SummaryPrimaryEmpty: string;
}

interface BookingFormSectionProps {
  children: React.ReactNode;
  showExpanded?: boolean;
  isStepDisabled?: boolean;
  step: Step;
  /** Title as React node (use FormattedString for i18n with bold/styled text) */
  title: React.ReactNode;
  messages: BookingFormSectionMessages;
  onExpand: VoidFunction;
  onNext: (step: Step) => void;
  onAnalyticsEvent?: (event: string, data?: Record<string, unknown>) => void;
  /** Optional icon component to show when step is complete */
  CheckIcon?: React.ComponentType<{ className?: string }>;
  /** Optional icon component for expand/collapse */
  ChevronIcon?: React.ComponentType<{ className?: string; isExpanded: boolean }>;
}

const getStepSummary = (step: Step) => {
  switch (step) {
    case 'step-1':
      return reservationStore.getStep1Summary();
    case 'step-2':
      return reservationStore.getStep2Summary();
    case 'step-3':
      return reservationStore.getStep3Summary();
  }
};

const getStepIsComplete = (step: Step) => {
  switch (step) {
    case 'step-1':
      return reservationStore.isStep1Complete();
    case 'step-2':
      return reservationStore.isStep2Complete();
    case 'step-3':
      return reservationStore.isStep3Complete();
  }
};

const getStepResetCallback = (step: Step) => {
  switch (step) {
    case 'step-1':
      return reservationStore.resetStep1;
    case 'step-2':
      return reservationStore.resetStep2;
    case 'step-3':
      return reservationStore.resetStep3;
  }
};

/** Default chevron icon */
const DefaultChevronIcon: React.FC<{ className?: string; isExpanded: boolean }> = ({
  className,
  isExpanded,
}) => (
  <svg
    className={clsx(className, 'transition', {
      'rotate-180': isExpanded,
    })}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/** Default check icon */
const DefaultCheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

export const BookingFormSection: React.FC<BookingFormSectionProps> = ({
  children,
  showExpanded = false,
  isStepDisabled = true,
  step,
  title,
  messages,
  onExpand,
  onNext,
  onAnalyticsEvent,
  CheckIcon = DefaultCheckIcon,
  ChevronIcon = DefaultChevronIcon,
}) => {
  const summary = getStepSummary(step);
  const isComplete = getStepIsComplete(step);
  const onReset = getStepResetCallback(step);

  const currentStep = parseInt(step.split('-')[1]) as StepNumber;

  const primaryActionMessage =
    currentStep !== 3 ? messages.nextButton : messages.continueButton;

  const onClick = () => {
    onAnalyticsEvent?.('button_click', {
      buttonSource: `step_${currentStep}`,
      buttonTag: 'next_step',
    });
    onNext(step);
  };

  return (
    <Tooltip text={isStepDisabled && messages.disabledTooltip}>
      <div
        className={clsx('relative w-full transition', {
          'opacity-50': isStepDisabled,
          'opacity-100': !isStepDisabled,
        })}
      >
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
        <legend
          onClick={onExpand}
          className={clsx(
            'hidden transition-all text-left sm:flex flex-row items-start sm:items-center gap-1 justify-between w-full',
            {
              'cursor-pointer': !isStepDisabled,
              'cursor-normal': isStepDisabled,
            },
          )}
        >
          <span className="flex font-serif flex-row flex-wrap items-center gap-2 uppercase text-xs sm:text-sm tracking-wider">
            <span>{title}</span>
            {isComplete ? <CheckIcon className="w-6 h-6" /> : null}
          </span>
          <div className="border-dotted flex-1 w-full border-b-2 mx-2 border-gray-300"></div>
          <Tooltip
            text={!showExpanded ? messages.expandTooltip : messages.collapseTooltip}
          >
            <ChevronIcon
              className="min-w-4 min-h-4 w-4 h-4 transition sm:pt-0 mt-1"
              isExpanded={showExpanded}
            />
          </Tooltip>
        </legend>
        {showExpanded ? (
          <div className="bg-white w-full sm:mt-3 rounded-lg">
            {children}
            <div className="mt-4 w-full flex flex-row items-center justify-end gap-3 px-6 pb-6">
              <button
                type="button"
                onClick={onReset}
                className="px-3 py-2 rounded-md bg-white"
              >
                {messages.resetButton}
              </button>
              <Tooltip text={!isComplete && messages.stepDisabledTooltip}>
                <button
                  disabled={!isComplete}
                  type="button"
                  className={clsx('px-3 py-2 rounded-md bg-green-200', {
                    'opacity-50': !isComplete,
                    'opacity-100': isComplete,
                  })}
                  onClick={onClick}
                >
                  {primaryActionMessage}
                </button>
              </Tooltip>
            </div>
          </div>
        ) : !isStepDisabled ? (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
          <div onClick={onExpand} className="w-full mt-2 mb-3" role="button">
            <StepSummarised summary={summary} messages={messages} />
          </div>
        ) : null}
      </div>
    </Tooltip>
  );
};

export const BookingFormSectionMobile: React.FC<BookingFormSectionProps> = ({
  children,
  showExpanded = false,
  isStepDisabled = true,
  step,
  title,
  messages,
  onExpand,
  CheckIcon = DefaultCheckIcon,
  ChevronIcon = DefaultChevronIcon,
}) => {
  const summary = getStepSummary(step);
  const isComplete = getStepIsComplete(step);

  return (
    <Tooltip text={isStepDisabled && messages.disabledTooltip}>
      <div
        className={clsx('relative w-full transition bg-yellow-50', {
          'opacity-50': isStepDisabled,
          'border-b-2 border-dotted': isStepDisabled,
          'border-t-2 border-dotted': step === 'step-1',
          'opacity-100': !isStepDisabled,
        })}
      >
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
        <legend
          onClick={onExpand}
          className={clsx(
            'transition-all text-left flex flex-row items-center gap-1 justify-between w-full px-4 py-4',
            {
              'cursor-pointer': !isStepDisabled,
              'cursor-normal': isStepDisabled,
            },
          )}
        >
          <span className="flex font-serif flex-row flex-wrap items-center gap-2 uppercase text-xs sm:text-sm tracking-wider">
            <span>{title}</span>
            {isComplete ? <CheckIcon className="w-6 h-6" /> : null}
          </span>
          <ChevronIcon
            className="min-w-4 min-h-4 w-4 h-4 transition mt-1"
            isExpanded={showExpanded}
          />
        </legend>
        {showExpanded ? (
          <div className="bg-white w-full rounded-lg pb-6">{children}</div>
        ) : !isStepDisabled ? (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
          <div onClick={onExpand} className="w-full" role="button">
            <StepSummarised summary={summary} messages={messages} />
          </div>
        ) : null}
      </div>
    </Tooltip>
  );
};

interface StepSummarisedProps {
  summary: StepSummary;
  messages: BookingFormSectionMessages;
}

const StepSummarised: React.FC<StepSummarisedProps> = ({ summary, messages }) => {
  switch (summary.step) {
    case 1:
      return <Step1Summarised {...summary} messages={messages} />;
    case 2:
      return <Step2Summarised {...summary} messages={messages} />;
    case 3:
      return <Step3Summarised {...summary} messages={messages} />;
  }
};

const EmptyText: React.FC<{ text: string }> = ({ text }) => {
  return <span className="text-[#666]">{text}</span>;
};

const Step1Summarised: React.FC<
  Step1Summary & { messages: BookingFormSectionMessages }
> = ({ rooms, messages }) => {
  const isRoomsEmpty = !rooms;

  return (
    <div className="flex flex-col gap-1 bg-white px-6 py-6 rounded-lg overflow-hidden">
      <p className="text-gray-500 text-sm">{messages.step1SummaryRooms}</p>
      <p>{isRoomsEmpty ? <EmptyText text={messages.step1SummaryRoomsEmpty} /> : rooms}</p>
    </div>
  );
};

const Step2Summarised: React.FC<
  Step2Summary & { messages: BookingFormSectionMessages }
> = ({ startDate, endDate, guests, messages }) => {
  const isStartDateEmpty = !startDate;
  const isEndDateEmpty = !endDate;
  const displayStartDate = startDate && displayDate(startDate).split('2025')[0].trim();
  const datesMessage = isStartDateEmpty ? (
    <EmptyText text={messages.step2SummaryDatesEmptyStart} />
  ) : isEndDateEmpty ? (
    <EmptyText text={messages.step2SummaryDatesEmptyEnd} />
  ) : (
    <p>
      {displayStartDate} {messages.step2SummaryDatesValue} {displayDate(endDate)}
    </p>
  );

  const isGuestsEmpty = !guests;
  const guestsMessage = isGuestsEmpty ? (
    <EmptyText text={messages.step2SummaryGuestsEmpty} />
  ) : (
    guests
  );

  return (
    <div className="flex flex-col gap-4 sm:gap-8 sm:flex-row items-start px-6 py-6 bg-white rounded-lg overflow-hidden">
      <div className="flex flex-col gap-1">
        <p className="text-gray-500 text-sm">{messages.step2SummaryDates}</p>
        <>{datesMessage}</>
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-gray-500 text-sm">{messages.step2SummaryGuests}</p>
        <p>{guestsMessage}</p>
      </div>
    </div>
  );
};

const Step3Summarised: React.FC<
  Step3Summary & { messages: BookingFormSectionMessages }
> = ({ guestDetails, primary, messages }) => {
  const isGuestDetailsEmpty = !guestDetails.some((g) => g);
  const isPrimaryContactEmpty = !primary.some((p) => p);

  return (
    <div className="flex flex-row flex-wrap gap-4 items-start sm:gap-8 px-6 py-6 bg-white rounded-lg overflow-hidden">
      <div className="flex flex-col gap-1">
        <p className="text-gray-500 text-sm">{messages.step3SummaryGuests}</p>
        {isGuestDetailsEmpty ? (
          <EmptyText text={messages.step3SummaryGuestsEmpty} />
        ) : (
          guestDetails.map((d) => <p key={d}>{d}</p>)
        )}
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-gray-500 text-sm">{messages.step3SummaryPrimary}</p>
        {isPrimaryContactEmpty ? (
          <EmptyText text={messages.step3SummaryPrimaryEmpty} />
        ) : (
          primary.map((d) => <p key={d}>{d}</p>)
        )}
      </div>
    </div>
  );
};
